import React, { useCallback, useEffect, useState } from 'react';
import { FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Input, Layout, PageHeader, Row, Select, Space, Spin, Table, Typography } from 'antd';
import { useHistory } from 'react-router-dom';
import { apiClient, apiErrorMessage } from '../../utils';
import moment from 'moment';
import { useForm } from 'antd/lib/form/Form';
import { DuplicateRecord, Grid, GridRow } from '../../models';
import { CellValue } from '../../elements';
import { RowDetailsPage } from '../grids';
import { FloatingModal } from '../../elements';

export function DuplicateRecordsReport() {
  const { Text } = Typography;
  const { Content } = Layout;
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [duplicateRecords, setDuplicateRecords] = useState<DuplicateRecord[]>();

  const [tableCurrent, setTableCurrent] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotal, setTableTotal] = useState(0);
  const [tableOrderBy, setTableOrderBy] = useState<string>();
  const [tableOrderDir, setTableOrderDir] = useState<'ASC' | 'DESC'>();
  const [tableFilters, setTableFilters] = useState<any>();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<any>(undefined);
  const [filterResetTrigger, setFilterResetTrigger] = useState(0);

  const [detailsModalDuplicateRecord, setDetailsModalDuplicateRecord] = useState<DuplicateRecord>();

  const onBackClick = () => {
    history.goBack();
  };

  const loadDuplicateRecords = useCallback(() => {
    setLoading(true);

    let offset = (tableCurrent - 1) * tablePageSize;
    const queryParams = [`limit=${tablePageSize}`, `offset=${offset}`];
    if (tableOrderBy) {
      queryParams.push(`orderBy=${tableOrderBy}`);
    }
    if (tableOrderDir) {
      queryParams.push(`orderDir=${tableOrderDir}`);
    }

    if (filterValues) {
      if (filterValues.status && filterValues.status !== 'ALL') {
        queryParams.push(`status=${filterValues.status}`);
      }
      if (filterValues.archieve && filterValues.archieve !== 'ACTIVE') {
        queryParams.push(`archieve=${filterValues.archieve}`);
      }
      if (filterValues.search) {
        queryParams.push(`search=${filterValues.search}`);
      }
    }

    const queryParamsStr = queryParams.join('&');

    apiClient
      .get(`duplicate-records?${queryParamsStr}`)
      .then((response: any) => {
        setDuplicateRecords(response.data.records);
        setTableTotal(response.data.count);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => setLoading(false));
  }, [filterValues, tableCurrent, tableOrderBy, tableOrderDir, tablePageSize]);

  useEffect(() => {
    if (detailsModalDuplicateRecord) {
      setDetailsModalDuplicateRecord(duplicateRecords?.find(dr => dr.id === detailsModalDuplicateRecord.id));
    }
  }, [detailsModalDuplicateRecord, duplicateRecords]);

  useEffect(() => {
    loadDuplicateRecords();
  }, [loadDuplicateRecords]);

  const onRefreshClick = () => {
    loadDuplicateRecords();
  };

  const onFiltersToggle = () => {
    setFiltersOpen(!filtersOpen);
  };

  const onClearFiltersClick = () => {
    setFilterValues(undefined);
    setTableFilters(undefined);
    setTableOrderBy(undefined);
    setTableOrderDir(undefined);
    setTableCurrent(1);
    setFilterResetTrigger(filterResetTrigger => filterResetTrigger + 1);
  };

  const onTableChange = (pagination: any, filters: any, sorter: any) => {
    setTableCurrent(pagination.current);
    setTablePageSize(pagination.pageSize);
    setTableOrderBy(sorter.columnKey);
    setTableOrderDir(!sorter.order ? undefined : sorter.order === 'descend' ? 'DESC' : 'ASC');
  };

  const onFiltersChange = (newFilterValues: any) => {
    setFilterValues(newFilterValues);
  };

  const onReviewClick = (duplicateRecord: DuplicateRecord) => {
    setDetailsModalDuplicateRecord(duplicateRecord);
  };

  const closeDetailsModal = () => {
    setDetailsModalDuplicateRecord(undefined);
  };

  const onReviewSaved = () => {
    loadDuplicateRecords();
  };

  const tableColumns = [
    {
      title: 'Veri seti',
      dataIndex: ['grid', 'name'],
      key: 'grid',
    },
    {
      title: 'Kayıt',
      dataIndex: 'id',
      key: 'duplicateRecord',
      render: (id: string, duplicateRecord: DuplicateRecord) => (
        <RecordColumn duplicateRecord={duplicateRecord} onReviewClick={() => onReviewClick(duplicateRecord)} />
      ),
    },
    {
      title: 'Benzerlik',
      dataIndex: 'ratio',
      key: 'ratio',
      sorter: true,
      render: (ratio: number) => '%' + ratio,
    },
    {
      title: 'Tespit tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (createdAt: Date) => moment(createdAt).format('DD.MM.yyyy HH:mm:ss'),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status: 'OPEN' | 'DUPLICATE' | 'NOT_DUPLICATE') => (
        <Text type={status === 'DUPLICATE' ? 'danger' : 'success'}>
          {status === 'DUPLICATE' ? 'Mükerrer' : status === 'NOT_DUPLICATE' ? 'Mükerrer değil' : null}
        </Text>
      ),
    },
    {
      title: 'Değerlendiren',
      dataIndex: ['reviewedBy', 'fullName'],
      key: 'reviewedBy',
    },
    {
      title: 'Değerlendirme',
      dataIndex: 'reviewedAt',
      key: 'reviewedAt',
      sorter: true,
      render: (reviewedAt: Date, duplicateRecord: DuplicateRecord) =>
        reviewedAt ? (
          moment(reviewedAt).format('DD.MM.yyyy HH:mm:ss')
        ) : (
          <Button size="small" onClick={() => onReviewClick(duplicateRecord)}>
            Değerlendir
          </Button>
        ),
    },
    {
      title: 'Açıklama',
      dataIndex: 'reviewMessage',
      key: 'reviewMessage',
    },
  ];

  return (
    <>
      <PageHeader
        title="Mükerrer kayıtlar"
        subTitle={`Toplam ${duplicateRecords?.length || 0} kayıt`}
        onBack={onBackClick}
        extra={[
          <>
            {(tableFilters || filterValues) && (
              <Button type="link" onClick={onClearFiltersClick}>
                Tüm filtreleri temizle
              </Button>
            )}
          </>,
          <Button key="filters" onClick={onFiltersToggle} icon={<FilterOutlined />}>
            Filtreleri {filtersOpen ? 'gizle' : 'göster'}
          </Button>,
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
        ]}
      />
      <Content>
        <FiltersForm hidden={!filtersOpen} onChange={onFiltersChange} resetTrigger={filterResetTrigger} />
        <Table
          columns={tableColumns}
          dataSource={duplicateRecords}
          loading={loading}
          scroll={{ x: '100%' }}
          pagination={{
            current: tableCurrent,
            total: tableTotal,
            pageSize: tablePageSize,
            pageSizeOptions: ['10', '25', '50', '100'],
            position: ['bottomCenter'],
            showTotal: (total, range) => `Toplam ${total} kayıt. ${range[0]}-${range[1]} arası gösteriliyor.`,
            showSizeChanger: true,
          }}
          onChange={onTableChange}
        />
      </Content>
      <FloatingModal
        title="Mükerrer kayıt detayları"
        visible={!!detailsModalDuplicateRecord}
        footer={null}
        onCancel={closeDetailsModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <DetailsPage duplicateRecord={detailsModalDuplicateRecord} onReviewSaved={onReviewSaved} />
      </FloatingModal>
    </>
  );
}

function FiltersForm(props: { hidden: boolean; resetTrigger: number; onChange: (filterValues: any) => void }) {
  const { Option } = Select;
  const [form] = useForm();

  useEffect(() => {
    form.resetFields();
  }, [form, props.resetTrigger]);

  const onChange = () => {
    const values = form.getFieldsValue();
    props.onChange(values);
  };

  if (props.hidden) {
    return null;
  }

  return (
    <Card style={{ marginBottom: '2rem' }}>
      <Form form={form} layout="inline">
        <Form.Item name="status">
          <Select allowClear placeholder="Durum" style={{ width: '12rem' }} onChange={onChange}>
            <Option value="ALL">Tümü</Option>
            <Option value="DUPLICATE">Mükerrer olanlar</Option>
            <Option value="NOT_DUPLICATE">Mükerrer olmayanlar</Option>
            <Option value="OPEN">Değerlendirilmeyenler</Option>
          </Select>
        </Form.Item>
        <Form.Item name="archieve">
          <Select allowClear placeholder="Arşiv" style={{ width: '12rem' }} onChange={onChange}>
            <Option value="ALL">Tümü</Option>
            <Option value="ACTIVE">Aktif olanlar</Option>
            <Option value="DELETED">Inaktif olanlar</Option>
          </Select>
        </Form.Item>
        <Form.Item name="search">
          <Input.Search onSearch={onChange} />
        </Form.Item>
      </Form>
    </Card>
  );
}

function RecordColumn(props: { duplicateRecord: DuplicateRecord; onReviewClick: () => void }) {
  if (!props.duplicateRecord) {
    return null;
  }

  const headers = Object.keys(props.duplicateRecord?.record1);
  const columnWidth = 100 / (headers.length + 1);

  const cellStyle: React.CSSProperties = {
    border: '1px',
    borderStyle: 'solid',
    borderColor: '#aaa',
    padding: '0.25rem',
    width: columnWidth + '%',
  };

  return (
    <table style={{ fontSize: '0.8rem', border: '1px solid #444', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {headers.map(h => (
            <th style={cellStyle}>{h}</th>
          ))}
          <th style={cellStyle}></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          {headers.map(h => (
            <td style={cellStyle}>{props.duplicateRecord.record1[h]}</td>
          ))}
          <td style={{ ...cellStyle, textAlign: 'center' }} rowSpan={2}>
            <Button type="link" size="small" onClick={props.onReviewClick}>
              Detaylar
            </Button>
          </td>
        </tr>
        <tr>
          {headers.map(h => (
            <td style={cellStyle}>{props.duplicateRecord.record2[h]}</td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

function DetailsPage(props: { duplicateRecord?: DuplicateRecord; onReviewSaved: () => void }) {
  const [duplicateRecord, setDuplicateRecords] = useState<DuplicateRecord>();

  useEffect(() => {
    setDuplicateRecords(props.duplicateRecord);
  }, [props.duplicateRecord]);

  if (!duplicateRecord) {
    return null;
  }

  return (
    <Row gutter={64}>
      <Col span={16}>
        <RecordsCompareTable duplicateRecord={duplicateRecord} />
      </Col>
      <Col span={8} style={{ borderLeft: '1px solid #ccc' }}>
        <ReviewForm duplicateRecord={duplicateRecord} onSaved={props.onReviewSaved} />
      </Col>
    </Row>
  );
}

function RecordsCompareTable(props: { duplicateRecord: DuplicateRecord }) {
  const { Text } = Typography;
  const [duplicateRecord, setDuplicateRecords] = useState<DuplicateRecord>();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<GridRow[]>();
  const [grid, setGrid] = useState<Grid>();
  const [rowDetailsModalOpen, setRowDetailsModalOpen] = useState<any>();

  useEffect(() => {
    setDuplicateRecords(props.duplicateRecord);
  }, [props.duplicateRecord]);

  const loadRecords = useCallback(() => {
    if (!duplicateRecord) {
      return;
    }

    const body = {
      gridId: duplicateRecord.grid.id,
      conditions: {
        logic: 'AND',
        items: [
          {
            objectType: 'record',
            objectId: 'MDM_RECORD',
            operator: 'recordIds',
            value: [duplicateRecord.record1Id, duplicateRecord.record2Id],
          },
        ],
      },
      limit: 0,
    };

    setLoading(true);
    apiClient
      .post(`records/records/query`, body)
      .then((response: any) => {
        const responseRecords = response.data.records as GridRow[];
        const responseGrid = response.data.grid as Grid;
        setRecords(responseRecords);
        setGrid(responseGrid);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [duplicateRecord]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const openRowDetailsModal = (row: GridRow) => {
    setRowDetailsModalOpen(row);
  };

  const closeRowDetailsModal = () => {
    setRowDetailsModalOpen(undefined);
  };

  const tdStyle: React.CSSProperties = {
    padding: '0.5rem',
    border: '1px solid #bbbbbb',
    wordBreak: 'break-all',
    width: '40%',
  };

  const thStyle: React.CSSProperties = {
    ...tdStyle,
    textAlign: 'left',
    backgroundColor: '#eeeeee',
    wordBreak: 'break-all',
    width: '20%',
  };

  if (!records) {
    return null;
  }

  return (
    <>
      <Spin spinning={loading}>
        <table style={{ minWidth: '100%' }}>
          <tbody>
            <tr>
              <th style={thStyle}>ID</th>
              {records?.map(r => (
                <td style={tdStyle} key={'id' + r.id}>
                  <Text copyable={{ text: r.id }}>
                    <Button type="text" size="small" onClick={() => openRowDetailsModal(r)}>
                      {r.id.substr(0, 2)}...{r.id.substr(r.id.length - 2, 2)}
                    </Button>
                  </Text>
                </td>
              ))}
            </tr>
            {grid?.fields?.map(f => (
              <tr key={f.id}>
                <th style={thStyle}>{f.name}</th>
                {records?.map(r => (
                  <td style={tdStyle} key={'' + f.id + r.id}>
                    <CellValue cell={r.cells[f.id]} originalValue={r.cells[f.id].value} field={f} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Spin>
      <FloatingModal
        title="Kayıt detayları"
        visible={rowDetailsModalOpen}
        footer={null}
        onCancel={closeRowDetailsModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <RowDetailsPage grid={grid} row={rowDetailsModalOpen} onRowAdded={() => {}} onSaved={(row: GridRow) => {}} onDelete={() => {}} />
      </FloatingModal>
    </>
  );
}

function ReviewForm(props: { duplicateRecord: DuplicateRecord; onSaved: () => void }) {
  const { Text } = Typography;
  const [duplicateRecord, setDuplicateRecords] = useState<DuplicateRecord>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setDuplicateRecords(props.duplicateRecord);
  }, [props.duplicateRecord]);

  if (!duplicateRecord) {
    return null;
  }

  const onFinish = (values: any) => {
    setLoading(true);
    apiClient
      .patch(`duplicate-records/${duplicateRecord.id}/review`, values)
      .then(() => {
        props.onSaved();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (duplicateRecord.reviewedAt) {
    return (
      <>
        <div style={{ marginBottom: '1rem' }}>
          <Text strong style={{ fontSize: '1.1rem' }}>
            Değerlendirme
          </Text>
        </div>
        <Space direction="vertical" style={{ paddingLeft: '1rem' }}>
          <Text type={duplicateRecord.status === 'DUPLICATE' ? 'danger' : 'success'}>
            {duplicateRecord.status === 'DUPLICATE' ? 'Mükerrer' : 'Mükerrer değil'}
          </Text>
          <Text>{duplicateRecord.reviewMessage}</Text>
          <Text type="secondary">
            {duplicateRecord.reviewedBy.fullName} - {moment(duplicateRecord.reviewedAt).format('DD.MM.yyyy HH:mm:ss')}
          </Text>
        </Space>
      </>
    );
  }

  return (
    <Form layout="vertical" name="basic" onFinish={onFinish}>
      <Form.Item label="Değerlendirme" name="result" rules={[{ required: true, message: 'Zorunlu alan' }]}>
        <Select>
          <Select.Option value="DUPLICATE">Mükerrer</Select.Option>
          <Select.Option value="NOT_DUPLICATE">Mükerrer değil</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item label="Açıklama" name="reviewMessage" rules={[{ max: 4000, message: 'En fazla 400 karakter' }]}>
        <Input.TextArea />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          Kaydet
        </Button>
      </Form.Item>
    </Form>
  );
}
